import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Modal,
  Box,
  TextField,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import config from '../config';

const server_URL = config.SERVER_URL;

const Servers = () => {
  const [open, setOpen] = useState(false);
  const [newServer, setNewServer] = useState({
    method: 'add',
    name: '',
    host: '',
    username: '',
    password: '',
    port: '',
    maxDailyEmails: '',
    customer_id: '',
  });
  const [servers, setServers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);
  const [customerFilter, setCustomerFilter] = useState('');
  const [testingId, setTestingId] = useState(null);
  const [toastState, setToastState] = useState({ open: false, message: '', severity: 'info' });

  const showToast = (message, severity = 'info') => {
    setToastState({ open: true, message, severity });
  };

  const handleOpen = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setNewServer({
      method: 'add',
      name: '',
      host: '',
      username: '',
      password: '',
      port: '',
      maxDailyEmails: '',
      customer_id: userRole === 2 ? loggedUserId : '',
    });
    setOpen(true);
  };

  const handleClose = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setNewServer({
      method: 'add',
      name: '',
      host: '',
      username: '',
      password: '',
      port: '',
      maxDailyEmails: '',
      customer_id: userRole === 2 ? loggedUserId : '',
    });
    setOpen(false);
  };

  useEffect(() => {
    fetchServers();
    // Get logged user info
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setLoggedUserId(userData.id || null);
    // If role is 2, set selected customer to logged user
    if (userData.role_id === 2) {
      setNewServer(prev => ({ ...prev, customer_id: userData.id }));
    }
  }, []);

  useEffect(() => {
    if (userRole !== null && loggedUserId !== null) {
      fetchCustomers();
    }
  }, [userRole, loggedUserId]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: 'list',
          user_id: loggedUserId,
          role_id: userRole
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomers(data.data);
        
        // Auto-select if only one customer
        if (data.data.length === 1) {
          setNewServer(prev => ({ ...prev, customer_id: data.data[0].id }));
        }
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchServers = async (filterCustomerId = null) => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'list',
        user_id: userData.id || null,
        role_id: userData.role_id || null
      };
      
      // Priority: explicit customer filter > role-based filtering
      // If role is 2, filter by logged user's customer_id (unless explicit filter is provided)
      if (filterCustomerId !== null && filterCustomerId !== '') {
        const customerIdValue = String(filterCustomerId) === '0' ? 0 : parseInt(filterCustomerId, 10);
        requestBody.customer_id = customerIdValue;
        console.log('Filtering by customer_id:', customerIdValue);
      } else if (userData.role_id === 2 && userData.id) {
        // For role 2 users, automatically filter by their own customer_id
        requestBody.customer_id = userData.id;
        console.log('Role 2 user - filtering by own customer_id:', userData.id);
      }
      
      console.log('Request body:', requestBody);
      
      const response = await fetch(server_URL + '/api/server', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch servers');
      }

      const data = await response.json();
      console.log('Received server data:', data);
      
      const serverData = Array.isArray(data) ? data : data.data || [];
      setServers(serverData);
      
      if (serverData.length === 0) {
        console.log('No servers found matching the filter criteria');
      } else {
        console.log(`Found ${serverData.length} server(s)`);
      }
    } catch (error) {
      console.error('Error fetching servers:', error);
      setServers([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleFilter = () => {
    fetchServers(customerFilter);
  };
  
  const handleClearFilter = () => {
    setCustomerFilter('');
    // For role 2 users, fetchServers will automatically filter by their own customer_id
    fetchServers();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate customer selection (allow 0 for role 1 users)
    const customerId = newServer.customer_id;
    if (customerId === '' || customerId === null || customerId === undefined) {
      alert('Please select a customer');
      return;
    }
    
    setIsSubmitting(true);
    const formData = new FormData(e.target);
    
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const serverData = {
      method: newServer.method,
      name: formData.get('name'),
      host: formData.get('host'),
      username: formData.get('username'),
      password: formData.get('password'),
      port: parseInt(formData.get('port')),
      maxDailyEmails: parseInt(formData.get('maxDailyEmails')),
      customer_id: customerId,
      user_id: userData.id,
      is_subuser: userData.is_subuser || false,
      subuser_id: userData.subuser_id || null,
    };

    if (newServer.method === 'update') {
      serverData.id = newServer.id;
    }

    try {
      const response = await fetch(`${server_URL}/api/server`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(serverData),
      });

      const data = await response.json();
      
      if (data.status === '1') {
        await fetchServers();
        handleClose();
      } else {
        alert(data.error || 'Failed to save server');
      }
    } catch (error) {
      console.error(`Error ${newServer.method}ing server:`, error);
      alert('An error occurred while saving the server');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTestConnection = async (id, host, username, password, port) => {
    console.log(id, host, username, password, port);
    setTestingId(id);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/server', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'test_connection',
          id: id,
          user_id: userData.id,
          host: host,
          username: username,
          password: password,
          port: port,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      const data = await response.json();
      if (data.status == 1) {
        showToast(data.message || 'Test email sent successfully.', 'success');
      } else {
        showToast(data.message || 'Failed to send test email.', 'error');
      }

      await fetchServers();
    } catch (error) {
      console.error('Error testing server connection:', error);
      showToast(error?.message || 'Error testing server connection', 'error');
    } finally {
      setTestingId(null);
    }
  };

  const handleEdit = (id, name, host, username, port, maxDailyEmails, customer_id) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    // For role 2 users, ensure customer_id is set to logged user
    const finalCustomerId = userRole === 2 
      ? (loggedUserId || (customer_id !== undefined && customer_id !== null ? Number(customer_id) : ''))
      : (customer_id !== undefined && customer_id !== null ? Number(customer_id) : '');
    
    setNewServer({
      method: 'update',
      id,
      name,
      host,
      username,
      password: '',
      port,
      maxDailyEmails,
      customer_id: finalCustomerId,
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this server?')) {
      return;
    }

    setDeletingId(id);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/server', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'delete',
          id: id,
          user_id: userData.id,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete server');
      }

      await fetchServers();
    } catch (error) {
      console.error('Error deleting server:', error);
      alert('Failed to delete server');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h1>Email server</h1>
        <Button variant="contained" color="primary" onClick={handleOpen}>
          Add Server
        </Button>
      </div>
      
      {/* Customer Filter - Only show for role 1 (admin) users */}
      {userRole === 1 && (
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center', 
          mb: 3,
          p: 2,
          bgcolor: '#f5f5f5',
          borderRadius: 1
        }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Filter by Customer</InputLabel>
            <Select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              label="Filter by Customer"
              size="small"
            >
              <MenuItem value="">All Customers</MenuItem>
              {/* <MenuItem value="0">Common (No Customer)</MenuItem> */}
              {customers.filter(customer => customer.id !== 0).map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.username} ({customer.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="contained"
            onClick={handleFilter}
            sx={{ height: '40px' }}
          >
            Filter
          </Button>
          {customerFilter !== '' && (
            <Button 
              variant="outlined"
              color="warning"
              onClick={handleClearFilter}
              sx={{ height: '40px' }}
            >
              Clear Filter
            </Button>
          )}
          <Button 
            variant="outlined"
            onClick={() => fetchServers(customerFilter)}
            sx={{ height: '40px' }}
          >
            Refresh
          </Button>
        </Box>
      )}
      
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading servers...</p>
          </div>
        </div>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {userRole === 1 && <TableCell>Customer</TableCell>}
                <TableCell>Host</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Port</TableCell>
                <TableCell>Max Daily Emails</TableCell>
                <TableCell>Verified</TableCell>
                <TableCell>Test Connection</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {servers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={userRole === 1 ? 9 : 8} align="center" sx={{ py: 5 }}>
                    <Box>
                      <h5 style={{ color: '#666' }}>No servers found</h5>
                      {customerFilter !== '' && (
                        <p style={{ color: '#999', fontSize: '0.875rem', marginTop: '8px' }}>
                          Try clearing the filter or selecting a different customer
                        </p>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                servers.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    {userRole === 1 && (
                      <TableCell>
                        {row.customer_username || 'Common (No Customer)'}
                        {row.customer_email && (
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            {row.customer_email}
                          </div>
                        )}
                      </TableCell>
                    )}
                    <TableCell>{row.host}</TableCell>
                    <TableCell>{row.username}</TableCell>
                    <TableCell>{row.port}</TableCell>
                    <TableCell>{row.max_daily_emails}</TableCell>
                    <TableCell>
                      {row.verified === 1 ? (
                        <span className="text-success">✅</span>
                      ) : (
                        <span className="text-danger" title={row.error_log}>❌ </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {testingId === row.id ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          <span style={{ fontSize: '0.875rem', color: '#666' }}>Please wait Testing Connection</span>
                        </div>
                      ) : (
                        <a href='#' onClick={(e) => {
                          e.preventDefault();
                          handleTestConnection(
                            row.id,
                            row.host,
                            row.username,
                            row.password,
                            row.port,
                          );
                        }}>Test</a>
                      )}
                    </TableCell>

                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => handleEdit(
                          row.id,
                          row.name,
                          row.host,
                          row.username,
                          row.port,
                          row.max_daily_emails,
                          row.customer_id
                        )}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(row.id)}
                        disabled={deletingId === row.id}
                      >
                        {deletingId === row.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Deleting...
                          </>
                        ) : (
                          'Delete'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="add-server-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <h2>{newServer.method === 'add' ? 'Add Server' : 'Edit Server'}</h2>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Server Name"
              name="name"
              defaultValue={newServer.name}
              required
            />
            <FormControl fullWidth required>
              <InputLabel>Customer</InputLabel>
              <Select
                value={newServer.customer_id === 0 ? 0 : (newServer.customer_id || '')}
                onChange={(e) => setNewServer(prev => ({ ...prev, customer_id: e.target.value === '' ? '' : Number(e.target.value) }))}
                label="Customer"
                disabled={userRole === 2} // Disable for role 2 users (only their own account)
              >
                {/* {userRole === 1 && (
                  <MenuItem value={0}>
                    Common (No Customer)
                  </MenuItem>
                )} */}
                {userRole === 1 ? (
                  // Role 1: Show all customers
                  customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.username} ({customer.email})
                    </MenuItem>
                  ))
                ) : (
                  // Role 2: Show only logged user
                  customers.filter(customer => customer.id === loggedUserId).map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.username} ({customer.email})
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Host (IP/Domain)"
              name="host"
              defaultValue={newServer.host}
              required
            />
            <TextField
              fullWidth
              label="Username/Email"
              name="username"
              type="email"
              defaultValue={newServer.username}
              required
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              defaultValue={newServer.password}
              required={newServer.method === 'add'}
            />
            <TextField
              fullWidth
              label="Port"
              name="port"
              type="number"
              defaultValue={newServer.port}
              required
            />
            <TextField
              fullWidth
              label="Max Daily Emails"
              name="maxDailyEmails"
              type="number"
              defaultValue={newServer.maxDailyEmails}
              required
            />
            <input type="hidden" name="customer_id" value={newServer.customer_id || ''} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {newServer.method === 'add' ? 'Adding...' : 'Updating...'}
                  </>
                ) : (
                  newServer.method === 'add' ? 'Add Server' : 'Update Server'
                )}
              </Button>
            </Box>
            </Stack>
          </form>
        </Box>
      </Modal>
      <Snackbar
        open={toastState.open}
        autoHideDuration={10000}
        onClose={() => setToastState(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToastState(prev => ({ ...prev, open: false }))}
          onClick={() => setToastState(prev => ({ ...prev, open: false }))}
          severity={toastState.severity}
          sx={{ width: '100%' }}
        >
          {toastState.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Servers;
